import {
  CategoryTag,
  DeleteCategoryInput,
  DeleteCategoryResponse,
  GetCategoriesInput,
  GetCategoriesResponse,
  InsertCategoryInput,
  InsertCategoryResponse,
  UpdateCategoryInput,
  UpdateCategoryResponse,
} from "@/src/models/category.model";
import { CATEGORIES_QUERY_KEY } from "@/src/query-keys/category.key";
import gqlClient from "@/src/services/api/graphql.client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import {
  DELETE_CATEGORY,
  GET_CATEGORIES,
  INSERT_CATEGORY,
  UPDATE_CATEGORY,
} from "./category.query";

const PAGE_SIZE = 6;

type GetCategoriesParams = {
  pageNo?: number;
  search?: string;
  tag: CategoryTag | null;
  pageSize?: number;
};

const useInvalidateCategories = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: [CATEGORIES_QUERY_KEY] });
};

const buildCategoryFilter = (
  search: string,
  tag: CategoryTag | null,
): string => {
  const filter: Record<string, unknown> = {};
  if (search) filter.categoryName = { $regex: search, $options: "i" };
  if (tag) filter.Tags = tag;
  return JSON.stringify(filter);
};

export const useGetCategoriesQuery = ({
  pageNo,
  search,
  tag,
  pageSize,
}: GetCategoriesParams) => {
  const filter = buildCategoryFilter(search ?? "", tag);

  return useQuery({
    queryKey: [CATEGORIES_QUERY_KEY, pageNo, search, tag],
    queryFn: async () => {
      const client = await gqlClient();
      const data = await client.request<GetCategoriesResponse>(GET_CATEGORIES, {
        filter,
        sort: "{}",
        pageNo: pageNo ?? 1,
        pageSize: pageSize ?? PAGE_SIZE,
      } satisfies GetCategoriesInput);
      return data.getcategorys;
    },
  });
};

export const useInsertCategoryMutation = () => {
  const invalidate = useInvalidateCategories();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (input: InsertCategoryInput) => {
      const client = await gqlClient();
      const data = await client.request<InsertCategoryResponse>(
        INSERT_CATEGORY,
        { categoryName: input.categoryName, tags: input.tags ?? [] },
      );
      return data.insertcategory;
    },
    onSuccess: () => {
      invalidate();
      Toast.show({ type: "success", text1: t("SUCCESS_CATEGORY_CREATED") });
    },
    onError: () => {
      Toast.show({ type: "error", text1: t("ERROR_CATEGORY_CREATION_FAILED") });
    },
  });
};

export const useUpdateCategoryMutation = () => {
  const invalidate = useInvalidateCategories();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (input: UpdateCategoryInput) => {
      const client = await gqlClient();
      const data = await client.request<UpdateCategoryResponse>(
        UPDATE_CATEGORY,
        input,
      );
      return data.updatecategory;
    },
    onSuccess: () => {
      invalidate();
      Toast.show({ type: "success", text1: t("SUCCESS_CATEGORY_UPDATED") });
    },
    onError: () => {
      Toast.show({ type: "error", text1: t("ERROR_CATEGORY_UPDATE_FAILED") });
    },
  });
};

export const useDeleteCategoryMutation = () => {
  const invalidate = useInvalidateCategories();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (input: DeleteCategoryInput) => {
      const client = await gqlClient();
      const data = await client.request<DeleteCategoryResponse>(
        DELETE_CATEGORY,
        input,
      );
      return data.deletecategory;
    },
    onSuccess: () => {
      invalidate();
      Toast.show({ type: "success", text1: t("SUCCESS_CATEGORY_DELETED") });
    },
    onError: () => {
      Toast.show({ type: "error", text1: t("ERROR_CATEGORY_DELETE_FAILED") });
    },
  });
};
